import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { GEAR_META, Gear } from '../domain/olmps';
import { makeThemedStyles, useTheme } from '../v10/theme';
import { Display, GlassCard, GoldButton } from '../v10/ui';
import { GearTile } from './primitives';
import {
  formatDirectSummary,
  formatOrientSummary,
  formatReviseSummary,
  formatRegulateSummary,
} from './useTodayData';
import { useSharedTodayData } from './TodayDataContext';

// The v8 "Step screen" — the ORDER table pre-filled for one METIS item. See
// docs/OLMPS_V8_SPEC.md "The two ORDER tables" → "Step screen".

export interface StepItem {
  id: string;
  rowId: string;
  text: string;
  time?: string;
  durationMinutes?: number;
}

interface ProtocolLite {
  id: string;
  title: string;
}

// Title-matching heuristic for "does a THEMIS protocol correspond to this
// METIS item" — there's no schema linkage between agenda_items and THEMIS
// protocols, so this does a case-insensitive equals/contains/contained-by
// match, same spirit as CompassView/useTodayData's sector name matching.
function protocolMatchesItem(protocolTitle: string, itemText: string): boolean {
  const a = protocolTitle.trim().toLowerCase();
  const b = itemText.trim().toLowerCase();
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

export function StepScreen({
  item,
  onClose,
  onOpenTool,
}: {
  item: StepItem;
  onClose: () => void;
  onOpenTool: (toolId: string, opts?: { initialProtocolId?: string }) => void;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;
  const today = useSharedTodayData();

  const [completed, setCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [protocols, setProtocols] = useState<ProtocolLite[]>([]);

  useEffect(() => {
    const match =
      (today.currentItem && today.currentItem.itemId === item.id && today.currentItem) ||
      (today.nextItem && today.nextItem.itemId === item.id && today.nextItem) ||
      null;
    if (match) setCompleted(match.completed);
  }, [today.currentItem, today.nextItem, item.id]);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setProtocols([]);
      return;
    }
    void supabase
      .from('protocols')
      .select('id, title')
      .eq('user_id', userId)
      .then(({ data }) => {
        if (!cancelled) setProtocols((data ?? []) as ProtocolLite[]);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const matchedProtocol = useMemo(
    () => protocols.find((p) => protocolMatchesItem(p.title, item.text)) ?? null,
    [protocols, item.text]
  );

  const toggleDone = useCallback(async () => {
    if (!userId || saving) return;
    setSaving(true);
    setSaveError(null);
    const next = !completed;
    setCompleted(next);
    const { error } = await supabase
      .from('agenda_items')
      .update({ completed: next, updated_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('id', item.rowId);
    setSaving(false);
    if (error) {
      setCompleted(!next);
      setSaveError(error.message);
    } else {
      today.refresh();
    }
  }, [userId, saving, completed, item.rowId, today]);

  const warn = today.flo?.mode === 'throttled';

  return (
    <View style={styles.root}>
      <ScrollView contentContainerStyle={styles.content}>
        <GlassCard style={styles.executeCard}>
          <View style={styles.executeHeader}>
            <GearTile letter={GEAR_META.execute.letter} active onPress={() => {}} />
            <Text style={styles.executeQuestion}>{GEAR_META.execute.question}</Text>
          </View>
          <Display size={26} style={styles.executeTitle}>{item.text || 'Untitled step'}</Display>
          {item.time || item.durationMinutes ? (
            <Text style={styles.executeMeta}>
              {item.time ? item.time : ''}
              {item.time && item.durationMinutes ? ' · ' : ''}
              {item.durationMinutes ? `${item.durationMinutes} min` : ''}
            </Text>
          ) : null}

          <GoldButton
            style={styles.protocolButton}
            label={matchedProtocol ? `Run protocol: ${matchedProtocol.title}` : 'Quick order (THEMIS)'}
            onPress={() =>
              matchedProtocol
                ? onOpenTool('themis', { initialProtocolId: matchedProtocol.id })
                : onOpenTool('themis')
            }
          />

          <Pressable
            style={[styles.doneButton, completed && styles.doneButtonActive]}
            onPress={() => void toggleDone()}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color={t.goldBright} />
            ) : (
              <Text style={[styles.doneButtonText, completed && styles.doneButtonTextActive]}>
                {completed ? 'Done ✓' : 'Mark done'}
              </Text>
            )}
          </Pressable>
          {saveError ? <Text style={styles.error}>{saveError}</Text> : null}
        </GlassCard>

        <CollapsedRow
          gear="orient"
          summary={today.loading ? 'Loading…' : formatOrientSummary(today)}
          onPress={() => onOpenTool('telos')}
        />
        <CollapsedRow
          gear="regulate"
          summary={today.loading ? 'Loading…' : formatRegulateSummary(today)}
          onPress={() => onOpenTool('numa')}
          warn={warn}
        />
        <CollapsedRow
          gear="direct"
          summary={today.loading ? 'Loading…' : formatDirectSummary(today)}
          onPress={() => onOpenTool('kyrs')}
        />
        <CollapsedRow
          gear="revise"
          summary={today.loading ? 'Loading…' : formatReviseSummary(today)}
          onPress={() => onOpenTool('clio')}
        />

        {today.error ? <Text style={styles.error}>{today.error}</Text> : null}
      </ScrollView>

      <Pressable style={styles.backButton} onPress={onClose}>
        <Text style={styles.backButtonText}>‹ Back to Today</Text>
      </Pressable>
    </View>
  );
}

function CollapsedRow({
  gear,
  summary,
  onPress,
  warn,
}: {
  gear: Gear;
  summary: string;
  onPress: () => void;
  warn?: boolean;
}) {
  const styles = useStyles();
  const meta = GEAR_META[gear];
  return (
    <Pressable style={styles.collapsedRow} onPress={onPress}>
      <GearTile letter={meta.letter} onPress={onPress} />
      <GlassCard style={[styles.collapsedCard, warn && styles.collapsedCardWarn]}>
        <Text style={styles.collapsedQuestion} numberOfLines={1}>
          {meta.question}
        </Text>
        <Text style={styles.collapsedSummary} numberOfLines={2}>
          {summary}
        </Text>
        {warn ? <Text style={styles.warnBadge}>Throttled</Text> : null}
      </GlassCard>
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    root: { flex: 1 },
    content: { padding: 16, gap: 12, paddingBottom: 24 },
    error: { color: t.lapsed, fontSize: 12, marginTop: 6 },

    executeCard: {
      borderRadius: 22,
      padding: 16,
      gap: 12,
    },
    executeHeader: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    executeQuestion: {
      flex: 1,
      fontSize: 13,
      fontWeight: '700',
      color: t.ivorySoft,
      fontStyle: 'italic',
      borderLeftWidth: 2,
      borderLeftColor: t.gold,
      paddingLeft: 10,
    },
    executeTitle: { textTransform: 'uppercase', letterSpacing: 2 },
    executeMeta: { fontSize: 13, fontWeight: '700', color: t.ivoryFaint },
    protocolButton: { marginTop: 4, height: 48, borderRadius: 24 },
    doneButton: {
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: 999,
      paddingVertical: 12,
      alignItems: 'center',
    },
    doneButtonActive: { backgroundColor: t.glass, borderColor: t.onPace, boxShadow: `0 0 12px ${t.onPace}55` },
    doneButtonText: { fontWeight: '800', fontSize: 14, color: t.ivorySoft },
    doneButtonTextActive: { color: t.onPace },

    collapsedRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    collapsedCard: {
      flex: 1,
      borderRadius: 22,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    collapsedCardWarn: { borderColor: t.behind },
    collapsedQuestion: { fontSize: 11, fontWeight: '700', color: t.ivoryFaint, fontStyle: 'italic' },
    collapsedSummary: { fontSize: 13, fontWeight: '800', color: t.ivory, marginTop: 2 },
    warnBadge: {
      marginTop: 4,
      alignSelf: 'flex-start',
      fontSize: 10,
      fontWeight: '900',
      color: t.onGold,
      backgroundColor: t.behind,
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 999,
      overflow: 'hidden',
    },

    backButton: {
      marginHorizontal: 16,
      marginBottom: 12,
      paddingVertical: 12,
      alignItems: 'center',
      borderWidth: 1,
      borderColor: t.navyLine,
      borderRadius: 999,
    },
    backButtonText: { fontWeight: '800', color: t.goldBright },
  }),
);
